import React from 'react';

import { getTheme, Theme } from '@endeavour/ui-kit/src/utils/theme';
import { shouldPolyfill as shouldPolyfillCanonicalLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill';
import { shouldPolyfill as shouldPolyfillListFormat } from '@formatjs/intl-listformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillPlurals } from '@formatjs/intl-pluralrules/should-polyfill';
import { shouldPolyfill as shouldPolyfillRelativeTime } from '@formatjs/intl-relativetimeformat/should-polyfill';
import { createRoot } from 'react-dom/client';

import { loadConfig } from './config/config';

import './styles/main.scss';

async function loadPluralPolyfills() {
    // TODO: Add locales support
    const locale = 'en-GB';
    if (!shouldPolyfillPlurals(locale)) {
        return;
    }

    // Load the polyfills
    await import('@formatjs/intl-pluralrules/polyfill');
    await import('@formatjs/intl-pluralrules/locale-data/en');
}

async function loadRelativeTimePolyfills() {
    // TODO: Add locales support
    const locale = 'en-GB';
    if (!shouldPolyfillRelativeTime(locale)) {
        return;
    }

    // Load the polyfills
    await import('@formatjs/intl-relativetimeformat/polyfill');
    await import('@formatjs/intl-relativetimeformat/locale-data/en');
}

async function loadLocalePolyfills() {
    if (!shouldPolyfillLocale()) {
        return;
    }

    await import('@formatjs/intl-locale/polyfill');
}

async function loadCanonicalLocalePolyfills() {
    if (!shouldPolyfillCanonicalLocales()) {
        return;
    }

    await import('@formatjs/intl-getcanonicallocales');
}

async function loadListFormatPolyfills() {
    const locale = 'en-GB';
    const unsupportedLocale = shouldPolyfillListFormat(locale);

    if (!unsupportedLocale) {
        return;
    }

    await import('@formatjs/intl-listformat/polyfill-force');
    /* @vite-ignore */
    await import('@formatjs/intl-listformat/locale-data/en');
}

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
function renderSite(Main: typeof import('./modules/main/main').Main) {
    const container = document.getElementById('root');

    if (!container) {
        throw new Error('Could not boostrap application as container element is missing.');
    }

    const root = createRoot(container);
    root.render(
        <React.StrictMode>
            <Main />
        </React.StrictMode>,
    );
}

function loadTheme() {
    const theme = getTheme();

    if (theme === Theme.AUTO) {
        return;
    }

    document.documentElement.setAttribute('data-theme', theme);
}

loadConfig()
    .then(() =>
        Promise.all([
            import('./modules/main/main'),
            loadPluralPolyfills(),
            loadRelativeTimePolyfills(),
            loadLocalePolyfills(),
            loadCanonicalLocalePolyfills(),
            loadListFormatPolyfills(),
            loadTheme(),
        ]),
    )
    .then(([mainModule]) => renderSite(mainModule.Main));
